import { h, ref } from 'vue'

import ConfirmDialogContent from '@/components/global/ConfirmDialogContent.vue'

import { eventEmitter } from '@/utils/events/event-emitter'

import { CommonEvent } from '@/constant/events'

import type { DialogConfig } from '@/types/common'

interface BaseDialogOptions {
  title: string
  text: string
  type?: 'primary' | 'danger'
  confirmButtonText?: string
  /** 是否可透過背景點擊、ESC、關閉按鈕關閉對話框（預設 true） */
  dismissible?: boolean
  dialogProps?: DialogConfig['dialogProps']
  onConfirm?: () => void | Promise<void>
}

interface ConfirmDialogOptions extends BaseDialogOptions {
  cancelButtonText?: string
  onCancel?: () => void
}

type AlertDialogOptions = BaseDialogOptions

export const useConfirmDialog = () => {
  const closeDialog = () => {
    eventEmitter.emit(CommonEvent.CLOSE_DIALOG)
  }

  const openDialog = (options: ConfirmDialogOptions & { showCancelButton: boolean }) => {
    const loading = ref(false)

    const handleConfirm = async () => {
      if (!options.onConfirm) {
        closeDialog()
        return
      }
      const result = options.onConfirm()
      if (result instanceof Promise) {
        loading.value = true
        try {
          await result
          closeDialog()
        } catch {
          // 非同步失敗時保持對話框開啟，讓使用者可重試
        } finally {
          loading.value = false
        }
      } else {
        closeDialog()
      }
    }

    const handleCancel = () => {
      options.onCancel?.()
      closeDialog()
    }

    const dismissibleProps =
      options.dismissible === false ? { closeOnClickModal: false, closeOnPressEscape: false, showClose: false } : {}

    eventEmitter.emit(CommonEvent.OPEN_DIALOG, {
      title: options.title,
      children: () =>
        h(ConfirmDialogContent, {
          type: options.type,
          text: options.text,
          confirmButtonText: options.confirmButtonText,
          cancelButtonText: options.cancelButtonText,
          showCancelButton: options.showCancelButton,
          loading: loading.value,
          onConfirm: handleConfirm,
          onCancel: handleCancel
        }),
      dialogProps: {
        width: '400px',
        ...dismissibleProps,
        ...options.dialogProps
      }
    })
  }

  /**
   * 確認對話框（雙按鈕）
   * - onConfirm 回傳 Promise 時自動顯示 loading，成功後自動關閉，失敗時保持開啟
   * - onCancel 執行後自動關閉
   */
  const confirm = (options: ConfirmDialogOptions) => {
    openDialog({ ...options, showCancelButton: true })
  }

  /**
   * 提示對話框（單按鈕）
   * - 預設不可透過背景點擊或 ESC 關閉
   * - onConfirm 回傳 Promise 時自動顯示 loading
   */
  const alert = (options: AlertDialogOptions) => {
    openDialog({
      ...options,
      showCancelButton: false,
      dismissible: options.dismissible ?? false
    })
  }

  return { confirm, alert }
}
